"use client";
import { hexToRgba } from "@/lib/hexToRgba";
import useWindowSize from "@/lib/hooks/useWIndowSize";
import Section from "@/lib/Layout/Wrappers/Section";
import { motion } from "framer-motion";
import Image from "next/image";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import Multiselect from "../Multiselect/Multiselect";
import SingleSelectInput from "../singleSelect/SingleSelectInput";
import SnappingSlider from "../SnappingSlider/SnappingSlider";
import SnappingSliderMood from "../SnappingSliderMood/SnappingSliderMood";
import TimePicker, { timeState } from "../Timepicker/Timepicker";
import ClockDial, { calculateTimeDifference } from "./ClockDial";
import { Controller, useForm } from "react-hook-form";
import VerticalLiquidSlider from "@/components/VerticalLiquidSlider/VerticalLiquidSlider";
import usePostBioFormData from "@/lib/hooks/QueryHooks/usePostBioFormData";
import SnappingSliderAlcohol from "@/components/SnappingSliderAlcohol/SnappingSliderAlcohol";
import Lottie from "lottie-react";
import completionAnimation from "../../assets/lotties/completion.json";
import SingleSelectPillInput from "@/components/SingleSelectPillInput/SingleSelectPillInput";
import { useToast } from "@chakra-ui/react";
import { BsCircleFill, BsLightbulbFill } from "react-icons/bs";
import PostSubmit from "./PostSubmit";
import { countryCode } from "@/lib/countryCodes";

type formModes =
  | "pre_init"
  | "init"
  | "form"
  | "end"
  | "calculation_screen"
  | "user_contact_section"
  | "post_submit";

const BiologicalForm = () => {
  const [modalState, setModalState] = useState(false);
  const [modalBgState, setModalBgState] = useState(false);
  const [biologicalAge, setBiologicalAge] = useState(0);
  const [countryCodeModalState, setCountryCodeModalState] = useState(false);
  const [ageDiff, setAgeDiff] = useState(0);
  const [stressCopingMechanism, setStressCopingMechanism] = useState<string[]>(
    []
  );
  const { mutate: bioFormMutate } = usePostBioFormData();
  const toast = useToast();
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      email: "", // Set the initial value for email
      mobile: "", // Set the initial value for mobile
      dob: "",
    },
  });
  const [selectedCountry, setSelectedCountry] = useState({
    name: "India",
    region: "Asia",
    timezones: {
      "Asia/Kolkata": "+05:30",
    },
    iso: {
      "alpha-2": "IN",
      "alpha-3": "IND",
      numeric: "356",
    },
    phone: ["+91"],
    emoji: "🇮🇳",
    image:
      "https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/IN.svg",
    phoneLength: 10,
  });
  const [countryCodeSearchInput, setCountryCodeSearchInput] = useState(``);
  const [formMode, setFormMode] = useState<formModes>("pre_init");
  const dimensions = useWindowSize();
  const [supplements, setSupplements] = useState<string[]>([]);
  const [fastStartTime, setFastStartTime] = useState<timeState>({
    hour: "22",
    min: "00",
  });
  const [fastEndTime, setFastEndTime] = useState<timeState>({
    hour: "10",
    min: "00",
  });
  const { hour: startHour, min: startMin } = fastStartTime;
  const { hour: endHour, min: endMin } = fastEndTime;
  const startTimeStr = startHour + ":" + startMin;
  const endTimeStr = endHour + ":" + endMin;
  const completeStates = new Set([
    "end",
    "calculation_screen",
    "user_contact_section",
    "post_submit",
  ]);
  const complete = completeStates.has(formMode);
  const [formIdx, setFormIdx] = useState(0);
  const [initIdx, setInitIdx] = useState(0);
  const [formState, setFormState] = useState({});
  const currentData = formData?.data?.[formIdx];
  const endScreenData = formData?.endScreen;
  const initData = formData?.initScreen?.[initIdx];
  const timeDifference = useMemo(
    () =>
      calculateTimeDifference(startTimeStr, endTimeStr)?.formattedOutput ??
      "00hr 00m",
    [startTimeStr, endTimeStr]
  );

  const onSubmit = (data) => {
    const currentDay = new Date().getTime();
    const selectedDay = new Date(data.dob).getTime();
    const eighteenYearsInEpoch = 60 * 1000 * 60 * 24 * 365 * 18;
    if (currentDay - selectedDay < eighteenYearsInEpoch) {
      toast({
        title: "Age should be greater than 18",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    let payload = {
      user: {
        email: data?.email,
        phone: data?.mobile
          ? selectedCountry?.phone + "-" + data?.mobile
          : null,
        dob: data?.dob,
      },
      answers: formState,
    };
    bioFormMutate(payload, {
      onSuccess: (res) => {
        console.log("successful", res);

        setBiologicalAge(res.data?.message?.biological_age);
        setAgeDiff(res.data?.message?.age_diff);
        setFormMode("post_submit");
        toast({
          title: "Sucessfully submitted",
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top",
        });
      },
      onError: (err) => {
        console.log("failed to  submit form", err);
        toast({
          title: "Failed to submit form",
          status: "error",
          duration: 2000,
          isClosable: true,
          position: "top",
        });
      },
    });
  };

  useEffect(() => {
    const storedData = localStorage.getItem("bioFormData");
    if (storedData) {
      const parsed = JSON.parse(storedData);
      setFormState(parsed);
      if ("supplements" in parsed) {
        const supplementsArr = parsed?.["supplements"]?.split(",");
        setSupplements(supplementsArr);
      }
      if ("coping_with_stress" in parsed) {
        const stress_coping_mech = parsed?.["coping_with_stress"]?.split(",");
        setStressCopingMechanism(stress_coping_mech);
      }
    }
  }, []);

  useEffect(() => {
    setFormState((p) => {
      return { ...p, fasting: String(timeDifference) };
    });
  }, [timeDifference]);

  const toggleModalState = () => {
    setModalState((p) => !p);
    setModalBgState((p) => !p);
  };
  const toggleCountryCodeModal = () => {
    setCountryCodeModalState((p) => !p);
    setModalBgState((p) => !p);
  };
  const toggleModalBgState = () => {
    setModalBgState((p) => !p);
  };

  const currentWhyItsImportant = currentData?.whyItsImportant;
  const questionKeys = currentData?.questions?.map(
    (question) => question.questionKey
  );

  const getInputType = (
    type: inputType,
    questionKey,
    options,
    theme: string[]
  ) => {
    switch (type) {
      case "single_select":
        return (
          <SingleSelectInput
            theme={theme}
            setFormState={setFormState}
            questionKey={questionKey}
            formState={formState}
            options={options}
          />
        );
      case "multi_select":
        if (questionKey === "supplements") {
          return (
            <Multiselect
              options={options}
              selected={supplements}
              data={formState}
              setData={setFormState}
              dataKey={questionKey}
              setSelected={setSupplements}
              theme={theme}
            />
          );
        } else if (questionKey === "coping_with_stress") {
          return (
            <Multiselect
              options={options}
              selected={stressCopingMechanism}
              data={formState}
              setData={setFormState}
              dataKey={questionKey}
              setSelected={setStressCopingMechanism}
              theme={theme}
            />
          );
        }
      case "single_select_pill":
        return (
          <SingleSelectPillInput
            theme={theme}
            setData={setFormState}
            dataKey={questionKey}
            data={formState}
            options={options}
          />
        );
      default:
        return <></>;
    }
  };
  const formLength = formData?.data?.length - 1;
  const initLength = formData?.initScreen.length - 1;
  const handleNext = () => {
    if (formIdx === formLength) {
      setFormMode("end");
    } else {
      setFormIdx((p) => p + 1);
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  };

  const handleNext1 = () => {
    if (initIdx === initLength) {
      setFormMode("form");
    } else {
      setInitIdx((p) => p + 1);
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  };

  const sectionTitle = currentData?.sectionTitle;
  const questions = currentData?.questions;
  const currentTheme = currentData?.theme;

  //----initVariable
  const initSectionTitle = initData?.sectionTitle;
  const initDescription = initData?.description;
  const initCurrentTheme = initData?.theme;

  const nextBtnDisabled = checkFilled(questionKeys, formState);

  const topCircleTitleToYMap = {
    "Diet and Nutrition": -670,
    "Fasting Duration": -450,
    "Bed Time": -670,
    "Alcohol Consumption & Smoking": -670,
    Activity: -670,
    Supplements: -670,
    "Stress & Mental Well Being": -670,
  };
  const formModeTopYMap: Record<formModes, number> = {
    init: -640,
    form: topCircleTitleToYMap[sectionTitle],
    end: -670,
    user_contact_section: -650,
    post_submit: -450,
  } as Record<formModes, number>;
  const topCircleY = formModeTopYMap[formMode] ?? -670;

  const gradientPercent = headingToGradientPercentMap[sectionTitle] ?? [
    "80%",
    "100%",
  ];

  const formStateTobgMap: Record<formModes, string> = {
    init: `linear-gradient(180deg, ${hexToRgba(initCurrentTheme, 1)} ${
      gradientPercent[0]
    }, ${hexToRgba(initCurrentTheme, 1)} ${gradientPercent[1]})`,
    form: `linear-gradient(180deg, ${hexToRgba(currentTheme[0], 1)} ${
      gradientPercent[0]
    }, ${hexToRgba(currentTheme[1], 1)} ${gradientPercent[1]})`,
    end: `linear-gradient(180deg, ${hexToRgba(
      endScreenData.theme[0],
      1
    )} ${"20%"}, ${hexToRgba(endScreenData.theme[1], 1)} ${"100%"})`,
    user_contact_section: `linear-gradient(180deg, ${hexToRgba(
      "#03f678",
      1
    )} ${"20%"}, ${hexToRgba("#03a678", 1)} ${"100%"})`,
    post_submit: `linear-gradient(180deg, ${hexToRgba(
      "#C16CD0",
      1
    )} ${"20%"}, ${hexToRgba("#570066", 1)} ${"100%"})`,
  } as Record<formModes, string>;
  const background = formStateTobgMap[formMode];

  const postSubmit = formMode === "post_submit" && (
    <motion.div
      animate={{ y: -40 }}
      className="flex justify-center flex-col items-center"
    >
      <p className="text-white font-light text-2xl">Your Biological Age is</p>
      <svg viewBox="0 0 200 200" style={{ height: 200, width: 200 }}>
        <circle
          cx={100}
          cy={100}
          r={80}
          fill="none"
          stroke="white"
          strokeWidth={8}
        />
        <text
          x={100}
          y={100 + 20}
          fontSize="4rem"
          textAnchor="middle"
          fontWeight={"bold"}
          fill="white"
        >
          {Math.round(biologicalAge)}
        </text>
      </svg>
      <p className="text-white font-medium w-4/5 text-center">
        {ageDiff > 0
          ? `You are ${ageDiff} years ahead
from your actual age.`
          : `You are ${Math.abs(ageDiff)} years younger
than your actual age.`}
      </p>
    </motion.div>
  );

  const clockInput = (
    <motion.div
      className="max-w-[500px] relative w-auto px-16 z-50"
      style={{}}
      animate={{ y: sectionTitle === "Fasting Duration" ? 120 : 0 }}
    >
      <motion.h3 className="text-5xl font-semibold text-white text-center ">
        {sectionTitle}
      </motion.h3>
      <div>
        {questions?.map((question, idx) => {
          const customInput = getInputType(
            question.type as inputType,
            question.questionKey,
            question.options,
            currentData.theme
          );
          return (
            <div key={question.question}>
              <h4 className="text-md font-medium my-4 text-white text-center ">
                {question.question}
              </h4>
              {customInput}
            </div>
          );
        })}
      </div>
      <div className="flex justify-center gap-12 my-4">
        <div>
          <p className=" text-center  text-sky-300 mb-2">Last Meal Time</p>
          <TimePicker timeState={fastStartTime} setTime={setFastStartTime} />
        </div>
        <div>
          <p className=" text-center text-sky-300 mb-2">First Meal Time</p>
          <TimePicker timeState={fastEndTime} setTime={setFastEndTime} />
        </div>
      </div>
      <motion.div
        className="mb-4 relative -z-50 flex justify-center"
        animate={{ y: -70 }}
      >
        {sectionTitle === "Fasting Duration" && (
          <ClockDial startTime={fastStartTime} endTime={fastEndTime} />
        )}
      </motion.div>
    </motion.div>
  );

  const sleepHourSlider = (
    <motion.div className="flex justify-center">
      <SnappingSlider setData={setFormState} dataKey={"sleep"} />
    </motion.div>
  );

  const moodSlider = (
    <motion.div className="flex justify-center items-center">
      <SnappingSliderMood setData={setFormState} dataKey={"stress_level"} />
    </motion.div>
  );

  const alcoholSlider = (
    <motion.div className="flex justify-center items-center">
      <SnappingSliderAlcohol setData={setFormState} dataKey={"alcohol"} />
    </motion.div>
  );
  const waterSlider = (
    <motion.div className="flex justify-center items-center">
      <VerticalLiquidSlider setData={setFormState} dataKey={"water"} />
    </motion.div>
  );

  const whyIsItImportant = !complete && (
    <motion.div
      className="flex flex-col"
      animate={{ y: sectionTitle === "Fasting Duration" ? 230 : 140 }}
    >
      <motion.div
        onClick={() => {}}
        className="opacity-0 cursor-pointer w-[300px] items-center rounded-full flex justify-between text-white bg-black text-center p-3 px-4 mb-8"
      >
        <div>
          <Image
            src={
              "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/WhyitsimportantQ.svg"
            }
            height={24}
            width={24}
            alt="icon"
          />
        </div>
        <div>Why is it important?</div>
        <div>
          <Image
            src={
              "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/Arrow.svg"
            }
            height={16}
            width={16}
            alt="icon"
          />
        </div>
      </motion.div>
    </motion.div>
  );

  const endScreen = (
    <Section className={`max-w-[500px] flex justify-center flex-col  `}>
      {/* <Lottie animationData={completionAnimation} loop={false} height={10} /> */}
      <motion.h3 className="text-5xl font-bold text-center text-white z-50 p-8  ">
        {endScreenData.title}
      </motion.h3>
      <p className="text-center my-2 z-50  text-white">
        {endScreenData.description}
      </p>
      <div className="px-4 flex-1 flex w-full bottom-0 mt-12  z-50 ">
        <button
          onClick={() => setFormMode("user_contact_section")}
          className="p-4 flex rounded-lg bg-[#222]  text-white flex-1 justify-center"
        >
          {endScreenData.btnText}
        </button>
      </div>
    </Section>
  );

  const modal = modalState && (
    <div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 w-5/6 top-1/2 -translate-y-1/2  justify-center z-50 bg-white border-[1px] rounded-lg p-8 shadow-lg">
      <button
        className="absolute right-4 top-4 text-lg font-bold"
        onClick={toggleModalState}
      >
        X
      </button>
      <h3 className="text-4xl font-bold text-center">{sectionTitle}</h3>

      <div>
        <Image
          src={currentWhyItsImportant?.image}
          height={200}
          width={200}
          alt="why its important"
        />
      </div>
      <div className="flex items-center gap-2">
        <p className="text-center">Did you know?</p>
        <BsLightbulbFill className="text-yellow-200 text-xl" />
      </div>
      <p className="text-center">{currentWhyItsImportant?.didYouKnow}</p>
      <ul className="flex flex-col gap-4">
        {currentWhyItsImportant?.tldr?.map((point) => (
          <li key={point} className="flex gap-2 items-start">
            <div>
              <BsCircleFill className="text-[8px]  translate-y-[6px]" />
            </div>
            <div>{point}</div>
          </li>
        ))}
      </ul>
    </div>
  );
  const modalBg = modalBgState && (
    <div
      className="bg-black h-[2000px] w-[2000px] opacity-20 top-0 -left-full absolute z-20"
      onClick={
        countryCodeModalState ? toggleCountryCodeModal : toggleModalBgState
      }
    ></div>
  );

  // for sectionTitle
  const sectionTitleComponent = !complete && (
    <>
      {sectionTitle !== "Fasting Duration" ? (
        <motion.h3
          className="text-2xl font-semibold text-white w-[300px] text-center"
          style={{ y: 80 }}
        >
          {sectionTitle}
        </motion.h3>
      ) : (
        <motion.div
          animate={{ y: sectionTitle === "Fasting Duration" ? 200 : 100 }}
        >
          {clockInput}
        </motion.div>
      )}
    </>
  );

  // for init sectionTitle
  const initSectionTitleComponent = (
    <motion.h3
      className="text-2xl font-semibold text-white w-[300px] text-center"
      style={{ y: 25 }}
    >
      {initSectionTitle}
    </motion.h3>
  );

  const userContactTop = formMode === "user_contact_section" && (
    <motion.div animate={{ y: -40 }}>
      <motion.h3
        className="text-4xl font-semibold text-white w-[300px] text-center"
        style={{}}
      >
        {"Hurray!"}
      </motion.h3>
      <p className="text-md font-medium text-white w-[300px] text-center my-2">
        Your biological age has been calculated.
      </p>
    </motion.div>
  );

  const userContactBottom = (
    <motion.div>
      <p className="my-4 font-medium">
        We need this to send you a detailed report
      </p>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label>Date of birth</label>
          <Controller
            name="dob"
            control={control}
            rules={{ required: "Date of Birth is required" }}
            render={({ field }) => (
              <div className="w-full">
                <input
                  type="date"
                  className="border-[1px] rounded-lg p-2 my-4 w-full"
                  {...field}
                />
                {errors.dob && (
                  <div className="text-red-500 mb-4">{errors.dob.message}</div>
                )}
              </div>
            )}
          />
        </div>
        <div>
          <label>Email</label>
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Email is required",
              pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
            }}
            render={({ field }) => (
              <div>
                <input
                  type="text"
                  placeholder="Type here"
                  className="border-[1px] rounded-lg p-2 my-4 w-full "
                  {...field}
                />
                {errors.email && (
                  <div className="text-red-500 mb-4">
                    {errors.email.message}
                  </div>
                )}
              </div>
            )}
          />
        </div>
        <div>
          <label>Mobile No. (optional)</label>
          <Controller
            name="mobile"
            control={control}
            rules={{
              pattern: /^\d{10}$/,
              required: "Mobile Number is required",
            }}
            render={({ field }) => (
              <div className="flex gap-2 items-center">
                <div
                  className="p-2 flex-[0.2] rounded-lg border-[1px] cursor-pointer flex justify-center"
                  onClick={toggleCountryCodeModal}
                >
                  {selectedCountry?.emoji} {selectedCountry?.phone}
                </div>
                <input
                  type="text"
                  placeholder="Type here"
                  className="border-[1px] flex-[0.8] w-full rounded-lg p-2 my-4 mb-4"
                  {...field}
                />
                {errors.mobile && (
                  <div className="text-red-500">{errors.mobile.message}</div>
                )}
              </div>
            )}
          />
        </div>
        <p className="font-medium my-8">
          Your health data will be safe and secure with us, All your data will
          be protected by Bonatra.
        </p>
        <button
          type="submit"
          disabled={!isValid}
          className={`left-0 px-4 p-4 w-full flex rounded-lg ${
            isValid ? "bg-[#222] text-white" : "bg-[#ddd] text-[#222]"
          }   flex-1 justify-center`}
        >
          Submit
        </button>
      </form>
    </motion.div>
  );

  const sectionIcon = currentData?.sectionIcon && !complete && (
    <motion.div className="fixed bg-white rounded-full p-4" animate={{ y: 24 }}>
      <Image
        src={currentData?.sectionIcon}
        height={30}
        width={30}
        alt={sectionTitle}
      />
    </motion.div>
  );
  const initSectionIcon = initData?.sectionIcon && (
    <motion.div className="fixed bg-white rounded-full p-4" animate={{ y: 24 }}>
      <Image
        src={initData?.sectionIcon}
        height={30}
        width={30}
        alt={initSectionTitle}
      />
    </motion.div>
  );
  const componentToRenderTitle =
    formMode === "pre_init" ? undefined : sectionTitleComponent;
  const componentToRenderIcon =
    formMode === "pre_init" ? undefined : sectionIcon;
  const componentToRenderButton = !complete && (
    <motion.button
      animate={{ y: sectionTitle === "Fasting Duration" ? 140 : 65 }}
      onClick={toggleModalState}
      style={{ color: "#222", backgroundColor: "#fff" }}
      className="p-2 border-2 rounded-full border-[#222] cursor-pointer font-semibold text-center mt-4"
    >
      Why is it important?
    </motion.button>
  );
  const topCircleAnimateY = formMode === "end" ? -60 : topCircleY;
  const TitleComponent =
    formMode === "init" ? initSectionTitleComponent : componentToRenderTitle;
  const IconComponent =
    formMode === "init" ? initSectionIcon : componentToRenderIcon;
  const ButtonComponent =
    formMode === "init" || formMode === "pre_init"
      ? undefined
      : componentToRenderButton;

  const topCircle = (
    <div className="flex flex-col items-center absolute left-1/2 ">
      <motion.div
        className="absolute top-0 max-w-[800px] min-h-[800px] flex flex-col justify-end items-center "
        style={{
          width: dimensions.width * 2,
          height: formMode === "end" ? "100vh" : "auto",
        }}
        initial={{ y: -1000 }}
        animate={{
          borderRadius: formMode === "end" ? 0 : "100%",
          background: background,
          y: topCircleAnimateY,
        }}
        transition={{ y: { type: "keyframes" } }}
      >
        {TitleComponent}
        {userContactTop}
        {whyIsItImportant}
        {IconComponent}
        {postSubmit}
        {ButtonComponent}
      </motion.div>
    </div>
  );

  const formTopMarginHeadingMap = {
    "Diet and Nutrition": 200,
    "Fasting Duration": dimensions.height - 100,
    "Bed Time": 200,
    "Alcohol Consumption & Smoking": 200,
    "Stress & Mental Well Being": 200,
    Activity: 200,
    Supplements: 200,
    Water: 200,
  };
  const topCircleMarginMap: Record<formModes, number> = {
    init: 140,
    form: formTopMarginHeadingMap[sectionTitle],
    end: 0,
    post_submit: 370,
    user_contact_section: 150,
  } as Record<formModes, number>;
  const topCircleMarginBottomFactor = topCircleMarginMap[formMode] ?? 100;
  const topSpace = (
    <div
      style={{
        height:
          sectionTitle === "Fasting Duration" ? 0 : topCircleMarginBottomFactor,
      }}
    ></div>
  );
  const form = (
    <div
      className={`${sectionTitle === "Fasting Duration" ? "h-42" : "h-auto"}`}
    >
      {sectionTitle !== "Fasting Duration" && (
        <div>
          {questions?.map((question, idx) => {
            const questionNo = idx + 1;
            const customInput = getInputType(
              question.type as inputType,
              question.questionKey,
              question.options,
              currentData.theme
            );
            return (
              <div key={question.question}>
                <h4 className="text-lg font-medium my-4 mt-6">
                  {questionNo}. {question.question}
                </h4>
                {sectionTitle === "Stress & Mental Well Being" &&
                  idx === 0 &&
                  moodSlider}
                {sectionTitle === "Bed Time" && sleepHourSlider}
                {sectionTitle === "Water" && waterSlider}
                {sectionTitle === "Alcohol Consumption & Smoking" &&
                  question.questionKey === "alcohol" &&
                  alcoholSlider}
                {customInput}
              </div>
            );
          })}
        </div>
      )}
      <div
        style={{
          width: "100%",
          maxWidth: 500,
          position: "fixed",
          paddingInline: 20,
        }}
        className={`${
          sectionTitle !== "Fasting Duration"
            ? "flex gap-4 left-1/2 -translate-x-1/2 my-8 w-full bottom-0"
            : " flex gap-4 my-8 w-full  left-1/2 -translate-x-1/2 bottom-0"
        }`}
      >
        {
          <button
            onClick={
              formIdx === 0
                ? () => setFormMode("init")
                : () => setFormIdx((p) => p - 1)
            }
            className="p-4 flex rounded-lg  text-black border-[1px] border-[#222] bg-white flex-1 justify-center"
          >
            Prev
          </button>
        }
        <button
          disabled={nextBtnDisabled}
          onClick={handleNext}
          className={`p-4 flex rounded-lg ${
            !nextBtnDisabled ? "bg-[#222] text-white" : "bg-[#ddd] text-[#222]"
          }   flex-1 justify-center`}
        >
          {formIdx === formLength ? "Finish" : "Next"}
        </button>
      </div>
    </div>
  );

  const preInitScreen = (
    <div className="h-auto">
      <Image
        src="https://storage.googleapis.com/bonatra-assets/Website%20Assets/Biologicalage.webp"
        alt="sunrise_img"
        fill
        style={{ objectFit: "cover" }}
      />

      <div
        style={{
          width: "100%",
          maxWidth: 420,
          position: "fixed",
          paddingInline: 20,
        }}
        className={`${
          sectionTitle !== "Fasting Duration"
            ? "flex flex-col gap-4 left-1/2 -translate-x-1/2 my-8 w-full bottom-0"
            : " flex gap-4 my-8 w-full left-1/2 -translate-x-1/2 bottom-0"
        }`}
      >
        <div className="text-white text-center mb-32">
          <h2 className="text-6xl font-medium my-6 font-bold">
            Unlock your true age.
          </h2>
          <p className="text-lg">
            Discover your biological age and embark on a journey to age in
            reverse
          </p>
        </div>
        <button
          onClick={() => setFormMode("init")}
          className={`p-4 flex rounded-lg bg-white text-black flex-1 justify-center font-semibold`}
        >
          Next
        </button>
      </div>
    </div>
  );

  const initScreen = (
    <div
      className={`${sectionTitle === "Fasting Duration" ? "h-42" : "h-auto"}`}
    >
      <div>
        <div className="mt-40">
          <h4 className="text-lg font-medium my-4 mt-6">{initDescription}</h4>
          {initSectionTitle === "Discover yourself." ? (
            <div className="flex flex-row mt-16">
              {formData?.initScreen[2]?.sectionIcons?.map((iconObj, id) => (
                <div
                  key={iconObj?.title}
                  className={`flex flex-col w-[33.3%] items-center font-semibold`}
                >
                  <h6>{iconObj?.section}</h6>
                  <img
                    className="mt-2 mb-3"
                    src={iconObj?.icon}
                    alt={iconObj?.title}
                  />
                  <p className="w-[50%] text-center">{iconObj?.title}</p>
                </div>
              ))}
            </div>
          ) : undefined}
          {initSectionTitle === "Discover yourself." ? (
            <p className="absolute bottom-28">
              {formData?.initScreen[2]?.description2}
            </p>
          ) : null}
        </div>
      </div>
      <div
        style={{
          width: "100%",
          maxWidth: 400,
          position: "fixed",
          paddingInline: 20,
        }}
        className={`${
          sectionTitle !== "Fasting Duration"
            ? "flex gap-4 left-1/2 -translate-x-1/2 my-8 w-full bottom-0"
            : " flex gap-4 my-8 w-full  left-1/2 -translate-x-1/2 bottom-0"
        }`}
      >
        {initIdx > 0 && (
          <button
            onClick={() => setInitIdx((p) => p - 1)}
            className="p-4 flex rounded-lg  text-black border-[1px] border-[#222] bg-white flex-1 justify-center"
          >
            Prev
          </button>
        )}
        <button
          onClick={handleNext1}
          className={`p-4 flex rounded-lg bg-black text-white flex-1 justify-center`}
        >
          {initSectionTitle === "Discover yourself." ? "Get Started" : "Next"}
        </button>
      </div>
    </div>
  );

  const postSubmitContent = <PostSubmit />;

  const contentMap: Record<formModes, ReactNode> = {
    pre_init: preInitScreen,
    init: initScreen,
    form: form,
    end: endScreen,
    post_submit: postSubmitContent,
    user_contact_section: userContactBottom,
  } as Record<formModes, ReactNode>;
  const content = contentMap[formMode];

  const handleSelectCountry = (country) => {
    setSelectedCountry(country);
  };
  const countryCodeSearchBar = (
    <div className="absolute border-[1px] w-96 z-50 shadow-lg flex justify-center overflow-y-scroll bg-white rounded-lg top-1/2 -translate-y-16 left-1/2 -translate-x-1/2 ">
      <input
        className="p-2 w-96"
        type="text"
        title="countryCode"
        defaultValue={selectedCountry.name + " " + `(${selectedCountry.phone})`}
        placeholder="Search"
        onChange={(e) => setCountryCodeSearchInput(e.target.value)}
        value={countryCodeSearchInput}
      ></input>
    </div>
  );
  const filteredCountryCodeList = countryCode?.filter(
    (el) =>
      el?.name
        ?.toLocaleLowerCase()
        .includes(countryCodeSearchInput.toLocaleLowerCase())
  );
  const countryCodeList = (
    <div className="absolute z-50 h-96 w-96 overflow-y-scroll shadow-lg bg-white rounded-lg p-4 top-1/2 left-1/2 -translate-x-1/2 -translate">
      {filteredCountryCodeList?.map((country) => {
        const isSelected = selectedCountry.name === country.name;
        return (
          <div
            key={country.name}
            className="p-2 cursor-pointer rounded-lg"
            style={{
              backgroundColor: isSelected ? "#222" : "#fff",
              color: isSelected ? "#FFF" : "#222",
            }}
            onClick={() => handleSelectCountry(country)}
          >
            {country.emoji} {country.name} ({country.phone})
          </div>
        );
      })}
    </div>
  );

  const countryCodeModal = countryCodeModalState && (
    <div>
      {countryCodeSearchBar}
      {countryCodeList}
    </div>
  );
  return (
    <Section
      style={{
        maxWidth: 500,
        overflowY:
          modalState || countryCodeModalState
            ? "hidden"
            : dimensions.width < 500
              ? "auto"
              : "scroll",
        backgroundColor: "white",
        overflowX: dimensions.width < 500 ? "hidden" : "hidden",
        position: "relative",
      }}
      className={`h-screen `}
    >
      {modal}
      {topCircle}
      {topSpace}
      {content}
      <div className="h-48"></div>
      {sectionTitle === "Diet and Nutrition" && !complete ? (
        <div className="h-48"></div>
      ) : (
        <></>
      )}
      {countryCodeModal}
      {modalBg}
    </Section>
  );
};

export default BiologicalForm;

const checkFilled = (questionKeys, formState) => {
  let filledCount = 0;
  questionKeys?.map((questionKey) => {
    if (questionKey in formState) {
      filledCount += 1;
    }
  });
  if (filledCount === questionKeys?.length) {
    return false;
  } else {
    return true;
  }
};
type inputType = "single_select" | "multi_select" | "single_select_pill";

const headingToGradientPercentMap = {
  "Diet and Nutrition": ["80%", "100%"],
  "Fasting Duration": ["50%", "100%"],
  "Bed Time": ["40%", "100%"],
  "Stress & Mental Well Being": ["50%", "100%"],
  Activity: ["30%", "100%"],
};

const formData = {
  initScreen: [
    {
      sectionIcon:
        "https://storage.googleapis.com/bonatra-assets/Website%20Assets/Difference.svg",
      sectionTitle: "What is Biological age?",
      theme: "#570066",
      icon: "",
      description:
        "Biological age, also known as physiological age, refers to the age at which an individual's body and its functional capabilities appear to be, as opposed to their chronological age, which is determined by the number of years they have been alive.",
      description2: "",
      sectionIcons: [],
    },
    {
      sectionIcon:
        "https://storage.googleapis.com/bonatra-assets/Website%20Assets/healthy-lifestyle.svg",
      sectionTitle: "Know the Difference",
      theme: "#2F555B",
      icon: "",
      description:
        "Biological age, also known as physiological age, refers to the age at which an individual's body and its functional capabilities appear to be, as opposed to their chronological age, which is determined by the number of years they have been alive.",
      description2: "",
      sectionIcons: [],
    },
    {
      sectionIcon:
        "https://storage.googleapis.com/bonatra-assets/Website%20Assets/quizhelp.svg",
      sectionTitle: "Discover yourself.",
      theme: "#42590F",
      icon: "",
      description:
        "Each section is related to your biological age and this will help you to change your lifestyle.",
      description2:
        "Bonatra assures you that your data is safe and secure with us",
      sectionIcons: [
        {
          icon: "https://assets.bonatra.com/Website%20Assets/Diet%26Nutrition.svg",
          section: "Section1",
          title: "Diet & Nutrition",
        },
        {
          icon: "https://assets.bonatra.com/Website%20Assets/Health%26Wellness.svg",
          section: "Section2",
          title: "Health & Wellness",
        },
        {
          icon: "https://assets.bonatra.com/Website%20Assets/Family%26Genetics.svg",
          section: "Section3",
          title: "Family & Genetics",
        },
      ],
    },
  ],
  endScreen: {
    theme: ["#03A678", "#03f678"],
    title: "You’ve made it",
    description: "Click the below button to calculate your Biological age.",
    icon: "",
    iconType: "lottie",
    btnText: "Calculate your Biological age",
  },
  bioAgeLoader: {},

  data: [
    {
      sectionIcon:
        "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/water-glass%201.svg",
      sectionTitle: "Water",
      theme: ["#2072F3", "#3AB0C9"],
      icon: "",
      description: "How many glasses of water do you consume on a daily basis?",
      questions: [
        {
          question:
            "How many glasses of water do you consume on a daily basis?",
          options: [
            { option: "Less than 2", mark: 2 },
            { option: "2 - 4", mark: 1 },
            { option: "5 - 6", mark: -1 },
            { option: "7+", mark: -2 },
          ],
          type: "?",
          questionKey: "water",
        },
      ],
      whyItsImportant: {
        tldr: [
          "Aquate water intake bolsters cellular health and curbs biological aging.",
          "Hydration is pivotal for cell function, fostering a youthful biological age.",
          "Drinking water detoxifies, revitalizes skin, and elevates cognition and energy.",
        ],
        didYouKnow:
          "An ideal water consumption for a healthy adult is about 8-10 glasses daily.",
        description:
          "Water consumption is a fundamental aspect of maintaining a younger biological age by supporting cellular function, overall health, and well-being. Staying properly hydrated is a simple yet essential part of a healthy lifestyle.",
        image:
          "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/Water%20Consumption%20Why%20its%20important.svg",
      },
    },
    {
      sectionIcon:
        "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/addiction%201.svg",
      sectionTitle: "Alcohol Consumption & Smoking",
      description: "",
      theme: ["#ff5955", "#ffd999"],
      whyItsImportant: {
        tldr: [
          "Alcohol, smoking, and toxins accelerate biological aging, deteriorating overall health.",
          "Their presence heightens the risk of age-related diseases.",
          "Consequently, they negatively impact biological age and reduce life quality.",
        ],
        didYouKnow:
          "Smokers may lose 10 years of life, while heavy drinkers can lose 5",
        description:
          "Both alcohol and smoking have detrimental effects on various aspects of health, and their negative impact on the aging process is well-documented. They can accelerate the biological aging of organs, tissues, and cells, leading to an increased risk of age-related diseases and a reduced quality of life.",
        image:
          "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/Alcohol%20%26%20Smoking%20Why%20its%20important.svg",
      },
      questions: [
        {
          question: "How's your alcohol consumption?",
          options: [
            { option: "More than 15 drinks per week", mark: 3 },
            { option: "11- 14 drinks per week", mark: 2 },
            { option: "5 - 10 drinks per week", mark: 0 },
            { option: "Less than 4 drinks per week", mark: -1 },
            { option: "None", mark: -1 },
          ],
          type: "?",
          questionKey: "alcohol",
        },
        {
          question: "Do you smoke?",
          options: [
            { option: "Current Smoker", mark: 2 },
            { option: "Ex Smoker", mark: 1 },
            { option: "Never Smoked", mark: -1 },
          ],
          type: "single_select",
          questionKey: "smoking",
        },
      ],
    },
    {
      sectionIcon:
        "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/sleeping1.svg",
      theme: ["#4700A4", "#0C85B3"],
      description: "",
      sectionTitle: "Bed Time",
      questions: [
        {
          question: "How many hours of sleep do you usually get each night?",
          options: [
            { option: "0–4 hours", mark: 3 },
            { option: "5–6 hours", mark: 2 },
            { option: "7–9 hours", mark: -2 },
            { option: "10+ hours", mark: 1 },
          ],
          type: "slider",
          questionKey: "sleep",
        },
      ],
      whyItsImportant: {
        tldr: [
          "Regular sleep routines directly influence a youthful biological age.",
          "Achieving 7-9 hours of sleep is vital for adult health and vitality.",
          "Prioritizing deep, quality sleep rejuvenates the body and mind.",
        ],
        didYouKnow:
          "The Bonatra Smart Ring X1 accurately tracks your sleep, aiding in healthier aging.",
        description:
          "To maintain a healthy biological age, it's important to establish and maintain consistent sleep patterns by going to bed and waking up at regular times, getting the recommended amount of sleep for your age group (usually 7-9 hours for adults), and ensuring the quality of your sleep.",
        image:
          "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/Bed%20Time%20Why%20its%20important.svg",
      },
    },
    {
      sectionIcon:
        "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/yoga1.svg",
      sectionTitle: "Stress & Mental Well Being",
      description: "",
      theme: ["#18BFFB", "#00940F"],
      questions: [
        {
          question: "How would you like to rate your stress levels?",
          options: [],
          type: "single_select_pill",
          questionKey: "stress_level",
        },
        {
          question: "How do you most commonly cope with high stress levels?",
          options: [
            { option: "Eating comfort food", mark: 2 },
            { option: "Smoking or drinking", mark: 3 },
            { option: "Browse Internet", mark: 1 },
            { option: "Meditation and journaling etc", mark: -2 },
            { option: "Physical activity", mark: -1 },
            { option: "Socialisation", mark: -1 },
            { option: "None", mark: -1 },
          ],
          type: "multi_select",
          questionKey: "coping_with_stress",
        },
      ],
      whyItsImportant: {
        tldr: [
          "Stress and mental well-being directly impact biological age.",
          "They govern numerous physiological and psychological functions.",
          "Managing stress and valuing mental health are keys to a youthful biological age.",
        ],
        didYouKnow:
          " Bonatra Ring X1 lets you practice 'mindfulness' and track vitals live.",
        description:
          "stress and mental well-being are integral to biological age because they influence a wide range of physiological and psychological processes. Prioritizing mental health and effectively managing stress are crucial for maintaining a younger biological age and overall well-being.",
        image:
          "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/Stress%20%26%20Mental%20Well%20Being%20Why%20its%20important.svg",
      },
    },
    {
      sectionIcon:
        "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/stretching1.svg",
      sectionTitle: "Activity",
      description:
        " How long your body gets to process food between meals can impact your health and aging.",
      theme: ["#5f55f6", "#F44336"],
      questions: [
        {
          question: "How many times do you exercise in a week?",
          options: [
            { option: "Less than 3 times", mark: 2 },
            { option: "3 to 4 times", mark: -1 },
            { option: "5 to 6 times", mark: -1 },
            { option: "7 times", mark: -1 },
          ],
          type: "single_select_pill",
          questionKey: "exercise",
        },
      ],
      whyItsImportant: {
        tldr: [
          "Physical activity slows biological aging by enhancing health and cellular function.",
          "Exercise, balanced diet, sleep, and stress management ensure a youthful biological age.",
          "This combination lowers the risk of age-related diseases.",
        ],
        didYouKnow:
          "Bonatra provides a 'Movement Index' to track your physical activity based on steps and more.",
        description:
          "Regular physical activity plays a crucial role in slowing down the biological aging process by positively influencing various aspects of health and cellular function. Combining exercise with a balanced diet, proper sleep, and stress management can help individuals maintain a more youthful biological age and reduce the risk of age-related diseases.",
        image:
          "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/Activity%20Why%20its%20important.svg",
      },
    },
    {
      sectionIcon:
        "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/supplement11.svg",
      sectionTitle: "Supplements",
      description:
        " How long your body gets to process food between meals can impact your health and aging.",
      theme: ["#ffd00d", "#ff900d"],
      questions: [
        {
          question:
            "Do you supplement your food with essential nutrients? (Can choose more than one supplement)",
          options: [
            { option: "Vitamin D", mark: 0 },
            { option: "Multivitamin", mark: 0 },
            { option: "Omega 3", mark: 0 },
            { option: "Vitamin B12", mark: 0 },
            { option: "None", mark: 0 },
          ],
          type: "multi_select",
          questionKey: "supplements",
        },
      ],
      whyItsImportant: {
        tldr: [
          "Supplements address specific health needs, aiding in biological age.",
          "Their use should be personalized, prioritizing quality and balanced nutrition.",
          "Supplements fill nutritional gaps, ensuring optimal health.",
        ],
        didYouKnow:
          "68% of adults take supplements, 50% daily for nutrient needs.",
        description:
          "Supplements can be important in terms of biological age by addressing specific health needs and supporting overall well-being, their use should be based on individualized assessments, high-quality products, and a balanced approach to nutrition and health",
        image:
          "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/Supplement%20Why%20its%20important.svg",
      },
    },
    {
      sectionIcon: undefined,
      sectionTitle: "Fasting Duration",
      description:
        "How long your body gets to process food between meals can impact your health and aging.",
      theme: ["#343434", "#000000"],
      whyItsImportant: {
        tldr: [
          "Intermittent fasting alternates eating and fasting; its duration impacts aging and should be guided by a health coach.",
          "Outcomes vary based on fasting type, health, and length.",
          "Longer fasts amplify benefits and risks.",
        ],
        didYouKnow:
          "30% see benefits from fasting, but prolonged periods risk adverse effects for 10%. Guidance is crucial.",
        description:
          "It's important to note that the effects of fasting on biological age can vary among individuals. Fasting is not a one-size-fits-all solution, and the outcomes can depend on factors like the type of fasting, duration, and individual health status.",
        image:
          "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/Fasting%20Time%20Why%20its%20important.svg",
      },
      questions: [
        {
          question: "How long do you fast in a day on average?",
          options: [
            { option: "<= 12", mark: 1 },
            { option: "12 - 14", mark: -1 },
            { option: "14+", mark: -2 },
          ],
          type: "clock_struct",
          questionKey: "fasting",
        },
      ],
    },

    {
      sectionIcon:
        "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/nutrition1.svg",
      sectionTitle: "Diet and Nutrition",
      theme: ["#945CB4", "#BA68C8"],
      icon: "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/nutrition1.svg",
      description: "",
      whyItsImportant: {
        tldr: [
          "Diet and nutrition significantly influence how our body ages internally.",
          "Biological age, often distinct from chronological age, is majorly swayed by dietary habits.",
          "Proper nutrition is paramount in maintaining a youthful biological age.",
        ],
        didYouKnow:
          "Studies indicate that 80% of people with a balanced diet have a biological age younger than their chronological age.",
        description:
          "Diet and nutrition play a significant role in determining biological age, which is a measure of how well your body is aging on a cellular and molecular level. Biological age can differ from your chronological age and is influenced by various factors, with diet and nutrition being among the most important.",
        image:
          "https://storage.googleapis.com/bonatra-assets/BonatraApplicationAssets/Diet%20and%20Nutrition%20Why%20its%20important.svg",
      },
      questions: [
        {
          question:
            "How many grams of vegetables do you consume on an average day (e.g., cruciferous, dark leafy, colourful vegetables)?",
          options: [
            { option: "None", mark: 3 },
            { option: "50 - 250 grams", mark: 2 },
            { option: "251 - 500 grams", mark: -2 },
            { option: "500+ grams", mark: -3 },
          ],
          type: "single_select_pill",
          questionKey: "vegetables",
        },
        {
          question:
            "How often do you consume fermented / prebiotic-rich foods (e.g. Dahi, Yogurt etc)?",
          options: [
            { option: "Once per month or less", mark: 2 },
            { option: "Once a week", mark: 0 },
            { option: "2 – 3 times a week", mark: -2 },
            { option: "Almost every day", mark: -3 },
          ],
          type: "single_select_pill",
          questionKey: "prebiotics",
        },

        {
          question:
            "How often do you consume refined flour or refined sugar (e.g., fast food, soda, packaged snacks, baked goods)?",
          options: [
            { option: "Almost every day", mark: 3 },
            { option: "2 – 3 times a week", mark: 2 },
            { option: "Once a week", mark: 0 },
            { option: "Once per month or less", mark: -1 },
          ],
          type: "single_select_pill",
          questionKey: "refined_foods",
        },
        {
          question:
            "How often do you consume at least one of the following protein-rich foods: paneer, chicken, eggs, lentils and legumes?",
          options: [
            { option: "Once per month or less", mark: 2 },
            { option: "Once a week", mark: 0 },
            { option: "2 – 3 times a week", mark: -2 },
            { option: "Almost every day", mark: -3 },
          ],
          type: "single_select_pill",
          questionKey: "protein",
        },
      ],
    },
  ],
};
